import React, { useRef, useMemo, useEffect, useCallback } from "react"
import { api } from "apiServer"
import { Layout } from "components/common"
import SwiperVideo from "components/SwiperVideo"
import classNames from "classnames"
import CardHorizontal from "components/TypeCard/CardHorizontal"
import TypeEvent from "utils/TypeEvent"
import { useDragScroll } from "hooks/subject/useDragScroll"
import { useIsMobile } from "hooks/subject/useIsMobile"
import { useScrollState } from "hooks/subject/useScrollState"
import { useTopicData } from "hooks/subject/useTopicData"
import { renderUrl } from "utils/helper"

// ─── Constants ───────────────────────────────────────────────────────────────
const PAGE_SIZE = 24
const PAGE_NUM = 1

// ─── Component: CategoryBar ───────────────────────────────────────────────────
function CategoryBar({ categories, activeCategory, onSelect, isMobile, scroll, name }) {
  const btnRefs = useRef([])
  const { ref, onTouchStart, onTouchMove } = useDragScroll()

  const handleSelect = useCallback(
    (id, index) => {
      onSelect(id)
      // Scroll the selected button into view (mobile tab row)
      if (index !== undefined && btnRefs.current[index]) {
        btnRefs.current[index].scrollIntoView({
          behavior: isMobile ? "smooth" : "auto",
          block: "nearest",
          inline: "start"
        })
      }
    },
    [onSelect]
  )

  const allBtn = (
    <button
      className={classNames(
        "flex-shrink-0 flex items-center justify-center list-game__background rounded-[44px] text-base font-medium py-2 px-4 gap-2 border border-[#5F5F5F] whitespace-nowrap",
        { "list-game__background--active": activeCategory === null }
      )}
      onClick={() => handleSelect(null)}
    >
      <img src={activeCategory === null ? "/icons/iconDocumentBlue.svg" : "/icons/iconDocument.svg"} alt="" />
      <span className={classNames({ "max-sm:hidden": activeCategory !== null })}>Tất cả</span>
    </button>
  )

  const categoryBtns = categories?.map((category, index) => (
    <button
      key={category?.id}
      ref={(el) => (btnRefs.current[index] = el)}
      className={classNames(
        "flex-shrink-0 flex items-center justify-center list-game__background rounded-[44px] text-base font-medium py-[2px] px-4 gap-2 border border-[#5F5F5F] whitespace-nowrap",
        { "list-game__background--active": activeCategory === category?.id }
      )}
      onClick={() => handleSelect(category?.id, index)}
    >
      <img
        src={activeCategory === category?.id ? category?.logo_active : category?.logo_inactive}
        width={36}
        height={36}
        alt=""
      />
      <span className={classNames({ "max-sm:hidden": activeCategory !== category?.id })}>{category?.name}</span>
    </button>
  ))

  if (isMobile) {
    return (
      <>
        {scroll && <div className="font-semibold text-2xl text-[#fafaf9] truncate">{name}</div>}
        <div
          ref={ref}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          className="flex overflow-x-auto gap-2 scrollbar-hide touch-pan-x"
        >
          {allBtn}
          {categoryBtns}
        </div>
      </>
    )
  }

  return (
    <div
      ref={ref}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      className="flex overflow-x-auto gap-2 scrollbar-hide touch-pan-x"
    >
      {allBtn}
      {categoryBtns}
    </div>
  )
}

// ─── Component: MyImage ───────────────────────────────────────────────────────
function MyImage({ base_url, thumbnail, name, description, backgroundColor, scroll, isMobile }) {
  return (
    <div className="relative rounded-3xl overflow-hidden max-sm:rounded-none">
      <div
        className="w-full h-[132px] max-sm:h-[42px] absolute bottom-0 left-0"
        style={{
          background: `linear-gradient(180deg, transparent 0%, ${backgroundColor} 84.66%, ${backgroundColor} 100%)`
        }}
      />
      <div className="absolute bottom-0 left-0 w-full">
        <div className="flex flex-col w-full gap-1 px-5 pb-5 max-sm:pb-3 max-sm:px-3">
          {(!scroll || !isMobile) && (
            <div className="font-semibold text-2xl text-[#fafaf9] max-sm:line-clamp-1">{name}</div>
          )}
          <div className="font-normal text-sm text-secondary line-clamp-1 max-sm:hidden">{description}</div>
        </div>
      </div>
      <div className="fix_img">
        <img
          className="w-full rounded-3xl h-full object-cover max-sm:rounded-none"
          src={renderUrl(base_url, thumbnail, "1920x800")}
          width={1578}
          height={358}
          alt={name}
        />
      </div>
    </div>
  )
}

// ─── Component: VideoGrid ─────────────────────────────────────────────────────
function VideoGrid({ items }) {
  if (!items?.length) return null
  return (
    <div className="flex flex-col gap-3.5">
      <span className="text-[24px] leading-[30px] text-white font-semibold max-sm:text-[16px]">Video</span>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6 max-lg:gap-3">
        {items.map((item) => (
          <CardHorizontal
            {...item}
            display_type={0}
            showTime={false}
            key={item.id}
            nextLink={`/video?id=${item.id}&type=${item.type}`}
            customGrid={true}
          />
        ))}
      </div>
    </div>
  )
}

// ─── Component: EmptyState ────────────────────────────────────────────────────
function EmptyState() {
  return (
    <div className="flex flex-col gap-2 items-center justify-center py-[193px] text-center">
      <span className="text-[22px] leading-[30px] font-semibold text-white">Nội dung chưa sẵn sàng</span>
      <span className="text-base leading-5 font-normal text-[#c6c6c8]">
        Chúng tôi đang cập nhật nội dung này. Bạn có thể thử lại sau một thời gian nữa.
      </span>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────
function Component({ topic, id }) {
  const scroll = useScrollState(100)
  const isMobile = useIsMobile(640)
  const containerRef = useRef(null)
  const loaderRef = useRef(null)

  const {
    topicMeta,
    categories,
    items,
    loading,
    hasMore,
    activeCategory,
    setActiveCategory,
    setPageNum,
    isFetchingRef
  } = useTopicData(id, topic)

  // Memoize events sorted so "live" events appear first
  const sortedEvents = useMemo(() => {
    if (!topicMeta?.events?.length) return []
    return [...topicMeta.events].sort((a, b) => (b.status === "live" ? 1 : 0) - (a.status === "live" ? 1 : 0))
  }, [topicMeta?.events])

  // Scroll container into view when category changes (not on first render)
  const isFirstCategoryRender = useRef(true)
  useEffect(() => {
    if (isFirstCategoryRender.current) {
      isFirstCategoryRender.current = false
      return
    }
    if (containerRef.current) {
      window.scrollTo({ top: containerRef.current.offsetTop - 80, behavior: "smooth" })
    }
  }, [activeCategory])

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!hasMore) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Guard: don't trigger while a fetch is already in progress
        if (entry.isIntersecting && !isFetchingRef.current && hasMore) {
          setPageNum((prev) => prev + 1)
        }
      },
      { threshold: 0.1, rootMargin: "200px" }
    )

    const loader = loaderRef.current
    if (loader) observer.observe(loader)

    return () => {
      if (loader) observer.unobserve(loader)
      observer.disconnect()
    }
  }, [hasMore, isFetchingRef, setPageNum])

  const isEmpty = !topicMeta?.events?.length && !topicMeta?.videos?.length && !items?.length

  return (
    <section
      ref={containerRef}
      className="mb-[46px] 3xl:ml-0 max-sm:mr-0 max-sm:ml-0 max-lg:mr-4 max-lg:ml-4 xl:mr-8 md:mx-4"
    >
      {/* Hero banner */}
      <div className="wapper__slide">
        <MyImage
          name={topicMeta?.name}
          thumbnail={topicMeta?.thumbnail_2 || topicMeta?.thumbnail_1 || topicMeta?.thumbnail_3}
          description={topicMeta?.description}
          backgroundColor={topicMeta?.backgroud_color}
          scroll={scroll}
          isMobile={isMobile}
        />
      </div>

      {/* Category tabs */}
      <div
        className={classNames(
          "list-game-container draggable flex gap-2 font-bold mt-6 sticky z-30 bg-[#131313] py-2 max-lg:top-[58.75px] max-lg:mt-5 max-sm:mt-0",
          !isMobile ? "items-center lg:top-[58.75px] xl:top-[83.75px]" : "flex-col top-[58.75px] max-sm:px-4"
        )}
      >
        <CategoryBar
          categories={categories}
          activeCategory={activeCategory}
          onSelect={setActiveCategory}
          isMobile={isMobile}
          scroll={scroll}
          name={topicMeta?.name}
        />
      </div>

      {/* Content area */}
      <div className="mx-auto overflow-x-hidden mt-8 max-sm:mt-2 max-sm:px-4">
        {sortedEvents.length > 0 && (
          <SwiperVideo name="Sự kiện" videos={sortedEvents} type_block={TypeEvent.LIVESHOW} />
        )}

        <VideoGrid items={items} />

        {/* Infinite scroll sentinel */}
        {hasMore && (
          <div ref={loaderRef} className="h-10 text-center text-sm text-gray-500">
            {loading ? "Đang tải dữ liệu..." : ""}
          </div>
        )}

        {isEmpty && !loading && <EmptyState />}
      </div>
    </section>
  )
}

export default Component
Component.Layout = Layout

// ─── SSR ──────────────────────────────────────────────────────────────────────
export async function getServerSideProps({ req, query, res }) {
  res.setHeader("Cache-Control", "public, s-maxage=60, stale-while-revalidate=300")

  const { cookies } = req
  const { id } = query
  let topic = {}

  try {
    const { data } = await api.getDetailSubject(cookies?.accessToken, id, {
      page_num: PAGE_NUM,
      page_size: PAGE_SIZE
    })
    topic = Object.keys(data).length > 0 ? data : {}
  } catch (error) {
    console.error("[SSR] getDetailSubject error:", error?.message)
  }

  return { props: { topic, id } }
}
